import React, { useState, useEffect } from 'react';

function csrfToken() {
  const el = document.querySelector('meta[name="csrf-token"]');
  return el ? el.content : '';
}

// @context TGW-1769 チャンネル作成モーダル（owner/manager のみ導線が出る。BE側でも403の二重防御）。
//   非公開時の初期メンバー候補には mention_candidates API を流用する（正会員一覧が引ける既存API
//   のため。専用APIを増やさない）。
export default function ChannelCreateModal({ circleId, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [memberQuery, setMemberQuery] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [members, setMembers] = useState([]); // {id, name}
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isPrivate) return undefined;
    const timer = setTimeout(async () => {
      const res = await fetch(
        `/api/message/mention_candidates?circle_id=${circleId}&q=${encodeURIComponent(memberQuery)}`,
        { credentials: 'same-origin', headers: { Accept: 'application/json' } },
      );
      if (res.ok) {
        const data = await res.json();
        setCandidates(data.candidates || []);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [isPrivate, memberQuery, circleId]);

  const submit = async () => {
    if (!name.trim()) {
      setError('チャンネル名を入力してください');
      return;
    }
    setSubmitting(true);
    setError(null);
    const params = new URLSearchParams();
    params.append('circle_id', circleId);
    params.append('channel_name', name.trim());
    params.append('description', description);
    params.append('is_private', isPrivate ? 'true' : 'false');
    members.forEach((m) => params.append('member_ids[]', m.id));

    const res = await fetch('/api/channels', {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken() },
      credentials: 'same-origin',
      body: params,
    });
    setSubmitting(false);
    if (res.ok) {
      const data = await res.json();
      onCreated(data.channel);
    } else {
      let message = 'チャンネルの作成に失敗しました';
      try {
        const data = await res.json();
        if (data.error) message = data.error;
      } catch (e) {
        /* noop */
      }
      setError(message);
    }
  };

  return (
    <div className="slackch-modal-overlay" onClick={onClose}>
      <div className="slackch-modal" onClick={(e) => e.stopPropagation()}>
        <div className="slackch-modal-header">
          チャンネルを作成
          <button
            type="button"
            className="slackch-modal-close"
            onClick={onClose}
            aria-label="閉じる"
          >
            ✕
          </button>
        </div>
        <div className="slackch-modal-body">
          {error && <div className="slackch-modal-error">{error}</div>}

          <label className="slackch-modal-label" htmlFor="slackch-create-name">
            チャンネル名
          </label>
          <input
            id="slackch-create-name"
            className="slackch-modal-input"
            type="text"
            value={name}
            maxLength={80}
            placeholder="例: 練習日程"
            onChange={(e) => setName(e.target.value)}
          />

          <label className="slackch-modal-label" htmlFor="slackch-create-desc">
            説明（任意）
          </label>
          <input
            id="slackch-create-desc"
            className="slackch-modal-input"
            type="text"
            value={description}
            placeholder="このチャンネルの用途"
            onChange={(e) => setDescription(e.target.value)}
          />

          <label
            className="slackch-modal-label"
            htmlFor="slackch-create-private"
          >
            公開設定
          </label>
          <label
            className="slackch-modal-toggle"
            htmlFor="slackch-create-private"
          >
            <input
              id="slackch-create-private"
              type="checkbox"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
            />
            🔒 非公開（招待した人だけが見られます）
          </label>

          {isPrivate && (
            <div className="slackch-modal-members">
              <label
                className="slackch-modal-label"
                htmlFor="slackch-create-member-q"
              >
                初期メンバー
              </label>
              {members.length > 0 && (
                <div className="slackch-modal-chips">
                  {members.map((m) => (
                    <span key={m.id} className="slackch-modal-chip">
                      {m.name}
                      <button
                        type="button"
                        onClick={() =>
                          setMembers((prev) =>
                            prev.filter((x) => x.id !== m.id),
                          )
                        }
                        aria-label={`${m.name}を外す`}
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <input
                id="slackch-create-member-q"
                className="slackch-modal-input"
                type="text"
                value={memberQuery}
                placeholder="名前で検索して追加"
                onChange={(e) => setMemberQuery(e.target.value)}
              />
              <div className="slackch-modal-candidates">
                {candidates
                  .filter((c) => !members.some((m) => m.id === c.id))
                  .slice(0, 8)
                  .map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() =>
                        setMembers((prev) => [
                          ...prev,
                          { id: c.id, name: c.name },
                        ])
                      }
                    >
                      {c.avatar_thumb ? (
                        <img src={c.avatar_thumb} alt="" />
                      ) : null}
                      {c.name}
                    </button>
                  ))}
              </div>
            </div>
          )}

          <div className="slackch-modal-actions">
            <button
              type="button"
              className="slackch-btn-ghost"
              onClick={onClose}
            >
              キャンセル
            </button>
            <button
              type="button"
              className="slackch-btn-primary"
              onClick={submit}
              disabled={submitting}
            >
              作成
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
